import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SlidersHorizontal } from 'lucide-react';
import { ActionBar, Container, PageHeader } from '@vnetwork/ui/layout';
import { Button, Input, Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { useToast } from '@vnetwork/ui/feedback';
import { useCreateLmsTranscodingPreset } from '@/api/media/live-presets';
import type { CreateLmsTranscodingPresetRequest } from '@/api/media/live-presets-types';
import { FormSectionCard } from '../event-jobs/event-job-create/FormSectionCard';
import styles from './PresetCreate.module.css';

type FormErrors = Partial<Record<'name' | 'bitrate' | 'gop' | 'fps' | 'width' | 'height', string>>;

const defaultForm: CreateLmsTranscodingPresetRequest = {
  name: '', description: '',
  video: { codec: 'libx264', bitrate: 600, fps: 24, gop: 48, width: 960, height: 540, profile: 'main' },
  audio: { codec: 'libfdk_aac', bitrate: 64, sample_rate: 44100, channels: 2 },
};

const NAME_RE = /^[a-zA-Z0-9_]{1,40}$/;

export interface LivePresetCreateProps {
  serviceId: string;
}

/** Create a new live transcoding preset (manual form). Navigates back to the presets list on success. */
export function LivePresetCreate({ serviceId }: LivePresetCreateProps) {
  const navigate = useNavigate();
  const { show } = useToast();

  const [form, setForm] = useState<CreateLmsTranscodingPresetRequest>(defaultForm);
  const [errors, setErrors] = useState<FormErrors>({});

  const createMut = useCreateLmsTranscodingPreset(serviceId, {
    onSuccess: () => { show({ tone: 'success', title: 'Transcoding preset created' }); navigate('..', { relative: 'path' }); },
    onError: (e) => show({ tone: 'danger', title: e.message || 'Failed to create preset' }),
  });

  const setField = (path: string, value: string | number) => {
    setForm((prev) => {
      const keys = path.split('.');
      if (keys.length === 1) return { ...prev, [path]: value };
      const section = prev[keys[0] as keyof CreateLmsTranscodingPresetRequest];
      return {
        ...prev,
        [keys[0]]: { ...(section as object), [keys[1]]: value },
      };
    });
  };

  const validate = (): boolean => {
    const e: FormErrors = {};
    if (!NAME_RE.test(form.name.trim())) e.name = 'Only letters, digits, underscores — max 40 characters.';
    if (form.video.bitrate <= 0) e.bitrate = 'Bitrate must be > 0.';
    if (form.video.gop <= 0) e.gop = 'GOP must be > 0.';
    if (form.video.fps <= 0) e.fps = 'FPS must be > 0.';
    else if (form.video.fps > 60) e.fps = 'FPS must be ≤ 60.';
    if (form.video.width <= 0) e.width = 'Width must be > 0.';
    if (form.video.height <= 0) e.height = 'Height must be > 0.';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;
    createMut.mutate([{ ...form, name: form.name.trim(), description: form.description.trim() }]);
  };

  return (
    <Container size="lg">
      <PageHeader
        icon={<SlidersHorizontal size={22} />}
        title="Add Transcoding Preset"
        description="Define a custom live transcoding preset — video codec, bitrate, resolution and audio settings."
      />

      <FormSectionCard title="Basic Information" description="Name and description for this preset.">
        <div className={styles.grid2}>
          <Field label="Name" required error={errors.name} description="Only [a-z, A-Z, 0-9, _], max 40 characters.">
            {(fp) => <Input {...fp} value={form.name} onChange={(e) => { setField('name', e.target.value); setErrors((p) => ({ ...p, name: undefined })); }} placeholder="Android_540P" />}
          </Field>
          <Field label="Description">
            {(fp) => <Input {...fp} value={form.description} onChange={(e) => setField('description', e.target.value)} placeholder="Android 540P" />}
          </Field>
        </div>
      </FormSectionCard>

      <FormSectionCard title="Video Configuration" description="Codec, bitrate, frame rate, and output size.">
        <div className={styles.grid4}>
          <Field label="Codec" required>
            {(fp) => (
              <Select value={form.video.codec} onValueChange={(v) => setField('video.codec', v)}>
                <SelectTrigger id={fp.id}><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="libx264">H.264</SelectItem>
                  <SelectItem value="libx265">H.265</SelectItem>
                </SelectContent>
              </Select>
            )}
          </Field>
          <Field label="Bitrate (kbps)" required error={errors.bitrate}>
            {(fp) => <Input {...fp} type="number" value={String(form.video.bitrate)} onChange={(e) => { setField('video.bitrate', Number(e.target.value)); setErrors((p) => ({ ...p, bitrate: undefined })); }} />}
          </Field>
          <Field label="Profile" required>
            {(fp) => (
              <Select value={form.video.profile} disabled>
                <SelectTrigger id={fp.id}><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="main">Main</SelectItem></SelectContent>
              </Select>
            )}
          </Field>
          <Field label="FPS" error={errors.fps}>
            {(fp) => <Input {...fp} type="number" value={String(form.video.fps)} onChange={(e) => { setField('video.fps', Number(e.target.value)); setErrors((p) => ({ ...p, fps: undefined })); }} />}
          </Field>
          <Field label="GOP" required error={errors.gop}>
            {(fp) => <Input {...fp} type="number" value={String(form.video.gop)} onChange={(e) => { setField('video.gop', Number(e.target.value)); setErrors((p) => ({ ...p, gop: undefined })); }} />}
          </Field>
          <Field label="Width" error={errors.width}>
            {(fp) => <Input {...fp} type="number" value={String(form.video.width)} onChange={(e) => { setField('video.width', Number(e.target.value)); setErrors((p) => ({ ...p, width: undefined })); }} />}
          </Field>
          <Field label="Height" error={errors.height}>
            {(fp) => <Input {...fp} type="number" value={String(form.video.height)} onChange={(e) => { setField('video.height', Number(e.target.value)); setErrors((p) => ({ ...p, height: undefined })); }} />}
          </Field>
        </div>
      </FormSectionCard>

      <FormSectionCard title="Audio Configuration" description="Audio codec, bitrate, sampling rate, and channels.">
        <div className={styles.grid4}>
          <Field label="Codec" required>
            {(fp) => (
              <Select value={form.audio.codec} onValueChange={(v) => setField('audio.codec', v)}>
                <SelectTrigger id={fp.id}><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="libfdk_aac">AAC</SelectItem></SelectContent>
              </Select>
            )}
          </Field>
          <Field label="Bitrate (kbps)" required>
            {(fp) => <Input {...fp} type="number" value={String(form.audio.bitrate)} onChange={(e) => setField('audio.bitrate', Number(e.target.value))} />}
          </Field>
          <Field label="Sample Rate" required>
            {(fp) => (
              <Select value={String(form.audio.sample_rate)} onValueChange={(v) => setField('audio.sample_rate', Number(v))}>
                <SelectTrigger id={fp.id}><SelectValue /></SelectTrigger>
                <SelectContent>
                  {[48000, 44100, 22050, 11025, 5512].map((r) => (
                    <SelectItem key={r} value={String(r)}>{(r / 1000).toFixed(r === 5512 ? 1 : 0)} kHz</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </Field>
          <Field label="Channels" required>
            {(fp) => (
              <Select value={String(form.audio.channels)} onValueChange={(v) => setField('audio.channels', Number(v))}>
                <SelectTrigger id={fp.id}><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="2">Stereo</SelectItem>
                  <SelectItem value="1">Mono</SelectItem>
                </SelectContent>
              </Select>
            )}
          </Field>
        </div>
      </FormSectionCard>

      <ActionBar
        start={<Button variant="secondary" onClick={() => navigate('..', { relative: 'path' })} disabled={createMut.isPending}>Cancel</Button>}
        end={<Button onClick={handleSubmit} disabled={createMut.isPending}>{createMut.isPending ? 'Creating…' : 'Create Preset'}</Button>}
      />
    </Container>
  );
}
